import { AlertTriangle, Download, History, Lightbulb, Link2, Pin, Plus, Search, Upload, UploadCloud } from "lucide-react";
import { useMemo, useState } from "react";
import { KNOWLEDGE_KIND_LABELS, KNOWLEDGE_KINDS, type KnowledgeKind } from "@navibiz/chat-core";
import { api } from "../../lib/api";
import { fmt, when } from "../../lib/format";
import { keys, useAction, useGaps, useKnowledge, useVersions } from "../../lib/queries";
import type { StoredEntry } from "../../lib/types";
import { Badge } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import { Dialog } from "../../ui/Dialog";
import { EmptyState, ErrorState, PageSkeleton, useToast } from "../../ui/Feedback";
import { Field, Input, Select } from "../../ui/Form";
import { EntryEditor, type EntryDraft } from "./EntryEditor";
import { ImportDialog } from "./ImportDialog";
import { useProjectContext } from "./ProjectLayout";
import styles from "./KnowledgeTab.module.css";

export function KnowledgeTab() {
  const { project } = useProjectContext();
  const knowledge = useKnowledge(project.id);
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<KnowledgeKind | "all">("all");
  const [editing, setEditing] = useState<EntryDraft | null>(null);
  const [importing, setImporting] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const entries = knowledge.data?.entries;
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (entries ?? []).filter(
      (entry) =>
        (kind === "all" || entry.kind === kind) &&
        (!needle || `${entry.title} ${entry.body} ${entry.tags.join(" ")}`.toLowerCase().includes(needle)),
    );
  }, [entries, query, kind]);

  if (knowledge.isPending) return <PageSkeleton />;
  if (knowledge.isError) return <ErrorState error={knowledge.error} />;
  const state = knowledge.data;
  const budgetRatio = state.draftTokens / state.fullContextBudget;

  return (
    <div className={styles.layout}>
      <div className={styles.main}>
        <div className={styles.status}>
          <div className={styles.statusText}>
            <div className={styles.statusTitle}>
              {state.unpublishedChanges ? (
                <>
                  <Badge tone="warning" icon={<AlertTriangle aria-hidden />}>
                    Draf berubah
                  </Badge>
                  Pengunjung masih melihat v{state.publishedVersion ?? "—"}. Terbitkan agar perubahan berlaku.
                </>
              ) : (
                <>
                  <Badge tone="good">Sinkron</Badge>
                  {state.publishedVersion ? `Versi v${state.publishedVersion} sedang tayang.` : "Belum pernah diterbitkan."}
                </>
              )}
            </div>
            <div className={styles.budget}>
              <div className={styles.budgetBar} role="meter" aria-valuenow={state.draftTokens} aria-valuemin={0} aria-valuemax={state.fullContextBudget} aria-label="Ukuran pengetahuan">
                <span style={{ width: `${Math.min(100, budgetRatio * 100)}%` }} className={budgetRatio > 1 ? styles.over : ""} />
              </div>
              <span className="num">
                ±{fmt.int(state.draftTokens)} token ·{" "}
                {state.contextMode === "full" ? "seluruhnya masuk prompt (jawaban paling lengkap, bisa di-cache)" : "mode pencarian: hanya potongan relevan yang dikirim"}
              </span>
            </div>
          </div>
          <div className={styles.statusActions}>
            <Button icon={<Upload aria-hidden />} onClick={() => setImporting(true)}>
              Impor
            </Button>
            <a className={styles.exportLink} href={`/admin/v1/projects/${project.id}/knowledge/export`} download>
              <Download aria-hidden /> Ekspor
            </a>
            <Button variant="primary" icon={<UploadCloud aria-hidden />} disabled={!state.unpublishedChanges && state.publishedVersion !== null} onClick={() => setPublishing(true)}>
              Terbitkan
            </Button>
          </div>
        </div>

        {state.missing.length > 0 && (
          <div className={styles.missing} role="status">
            <AlertTriangle aria-hidden />
            Belum ada butir untuk: <strong>{state.missing.map((k) => KNOWLEDGE_KIND_LABELS[k]).join(", ")}</strong>. Pengunjung paling sering menanyakan hal-hal ini.
          </div>
        )}

        <div className={styles.filters}>
          <label className={styles.search}>
            <Search aria-hidden />
            <input placeholder="Cari judul, isi, atau tag…" value={query} onChange={(event) => setQuery(event.target.value)} aria-label="Cari pengetahuan" />
          </label>
          <Select value={kind} onChange={(event) => setKind(event.target.value as KnowledgeKind | "all")} aria-label="Jenis" className={styles.kindSelect}>
            <option value="all">Semua jenis ({state.entries.length})</option>
            {KNOWLEDGE_KINDS.map((k) => (
              <option key={k} value={k}>
                {KNOWLEDGE_KIND_LABELS[k]} ({state.entries.filter((entry) => entry.kind === k).length})
              </option>
            ))}
          </Select>
          <Button variant="primary" icon={<Plus aria-hidden />} onClick={() => setEditing({ kind: kind === "all" ? "faq" : kind, title: "", body: "", tags: [], pinned: false })}>
            Butir baru
          </Button>
        </div>

        {filtered.length === 0 ? (
          <Card>
            <EmptyState title={state.entries.length ? "Tidak ada yang cocok" : "Basis pengetahuan masih kosong"}>
              {state.entries.length
                ? "Ubah kata kunci atau jenisnya."
                : "Mulai dari formulir basis pengetahuan klien, atau impor file knowledge.json yang dihasilkan template website Navibiz."}
            </EmptyState>
          </Card>
        ) : (
          KNOWLEDGE_KINDS.filter((k) => filtered.some((entry) => entry.kind === k)).map((k) => (
            <section key={k} className={styles.group}>
              <h3 className={styles.groupTitle}>
                {KNOWLEDGE_KIND_LABELS[k]} <span>{filtered.filter((entry) => entry.kind === k).length}</span>
              </h3>
              <div className={styles.entries}>
                {filtered
                  .filter((entry) => entry.kind === k)
                  .map((entry) => (
                    <EntryCard key={entry.id} entry={entry} onOpen={() => setEditing(entry)} />
                  ))}
              </div>
            </section>
          ))
        )}
      </div>

      <aside className={styles.side}>
        <GapsPanel projectId={project.id} onAdd={(question) => setEditing({ kind: "faq", title: question, body: "", tags: [], pinned: false })} />
        <VersionsPanel projectId={project.id} current={state.publishedVersion} />
      </aside>

      <EntryEditor projectId={project.id} linkage={project.linkage.enabled} draft={editing} onClose={() => setEditing(null)} />
      <ImportDialog projectId={project.id} linkage={project.linkage.enabled} open={importing} onClose={() => setImporting(false)} />
      <PublishDialog projectId={project.id} open={publishing} onClose={() => setPublishing(false)} entries={state.entries.length} tokens={state.draftTokens} />
    </div>
  );
}

function EntryCard({ entry, onOpen }: { entry: StoredEntry; onOpen: () => void }) {
  return (
    <button type="button" className={styles.entry} onClick={onOpen}>
      <div className={styles.entryHead}>
        <span className={styles.entryTitle}>{entry.title}</span>
        <span className={styles.entryIcons}>
          {entry.link && (
            <span className={styles.linked} title={`Rujukan terkait: ${entry.link}`}>
              <Link2 aria-hidden /> <span className="sr-only">Punya rujukan terkait</span>
            </span>
          )}
          {entry.pinned && (
            <span className={styles.pinned} title="Selalu dikirim ke model">
              <Pin aria-hidden /> <span className="sr-only">Disematkan</span>
            </span>
          )}
        </span>
      </div>
      <p className={styles.entryBody}>{entry.body}</p>
      <div className={styles.entryFoot}>
        <span className={styles.tags}>
          {entry.tags.slice(0, 4).map((tag) => (
            <span key={tag}>#{tag}</span>
          ))}
        </span>
        <span>{when.ago(entry.updatedAt)}</span>
      </div>
    </button>
  );
}

function GapsPanel({ projectId, onAdd }: { projectId: string; onAdd: (question: string) => void }) {
  const gaps = useGaps(projectId, 30);
  return (
    <Card title="Celah pengetahuan" description="Pertanyaan yang dialihkan karena bot tidak tahu, atau jawabannya dinilai kurang tepat (30 hari).">
      {gaps.isPending ? null : !gaps.data?.clusters.length ? (
        <EmptyState icon={<Lightbulb aria-hidden />} title="Tidak ada celah">
          Semua pertanyaan pengunjung terjawab dari pengetahuan.
        </EmptyState>
      ) : (
        <ul className={styles.gaps}>
          {gaps.data.clusters.map((cluster) => (
            <li key={cluster.label}>
              <div>
                <div className={styles.gapLabel}>“{cluster.label}”</div>
                <div className={styles.gapMeta}>
                  {fmt.int(cluster.count)}× ditanyakan{cluster.unanswered ? ` · ${cluster.unanswered} tak terjawab` : ""}
                </div>
              </div>
              <Button size="sm" icon={<Plus aria-hidden />} onClick={() => onAdd(cluster.label)}>
                Jadikan FAQ
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function VersionsPanel({ projectId, current }: { projectId: string; current: number | null }) {
  const versions = useVersions(projectId);
  const toast = useToast();
  const restore = useAction((number: number) => api.post(`/projects/${projectId}/knowledge/versions/${number}/restore`), [keys.knowledge(projectId), keys.project(projectId)]);
  return (
    <Card title="Riwayat versi" description="Setiap terbit membekukan pengetahuan. Pulihkan ke draf bila perlu kembali.">
      {!versions.data?.length ? (
        <EmptyState icon={<History aria-hidden />} title="Belum ada versi" />
      ) : (
        <ol className={styles.versions}>
          {versions.data.map((version) => (
            <li key={version.number}>
              <div className={styles.versionHead}>
                <strong>v{version.number}</strong>
                {version.number === current && <Badge tone="good">tayang</Badge>}
                <span className={styles.versionMeta}>{when.dateTime(version.createdAt)}</span>
              </div>
              <div className={styles.versionMeta}>
                {version.entryCount} butir · ±{fmt.int(version.tokens)} token{version.publishedBy ? ` · ${version.publishedBy}` : ""}
              </div>
              {version.note && <div className={styles.versionNote}>{version.note}</div>}
              {version.number !== current && (
                <Button
                  size="sm"
                  variant="ghost"
                  loading={restore.isPending && restore.variables === version.number}
                  onClick={async () => {
                    if (!confirm(`Ganti draf saat ini dengan isi v${version.number}? Draf lama diarsipkan.`)) return;
                    await restore.mutateAsync(version.number);
                    toast(`Isi v${version.number} dipulihkan ke draf. Terbitkan untuk menayangkannya.`);
                  }}
                >
                  Pulihkan ke draf
                </Button>
              )}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function PublishDialog({ projectId, open, onClose, entries, tokens }: { projectId: string; open: boolean; onClose: () => void; entries: number; tokens: number }) {
  const toast = useToast();
  const [note, setNote] = useState("");
  const publish = useAction(
    (body: { note: string }) => api.post<{ version: number }>(`/projects/${projectId}/knowledge/publish`, body),
    [keys.knowledge(projectId), keys.versions(projectId), keys.project(projectId)],
  );
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Terbitkan pengetahuan"
      description="Pengunjung langsung dijawab dari versi baru. Versi lama tetap tersimpan di riwayat."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            variant="primary"
            loading={publish.isPending}
            onClick={async () => {
              const result = await publish.mutateAsync({ note });
              toast(`Versi v${result.version} tayang.`);
              setNote("");
              onClose();
            }}
          >
            Terbitkan sekarang
          </Button>
        </>
      }
    >
      <p style={{ marginBottom: 16 }}>
        {entries} butir · ±{fmt.int(tokens)} token. Sebaiknya jalankan uji terima ulang setelah perubahan besar.
      </p>
      <Field label="Catatan versi" hint="Contoh: “Harga 2027 dan jam buka Ramadan”.">
        {(id, describedBy) => <Input id={id} value={note} maxLength={300} onChange={(event) => setNote(event.target.value)} aria-describedby={describedBy} />}
      </Field>
    </Dialog>
  );
}
